import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Checkbox, Popover, Tooltip, Typography } from "antd";
import { HolderOutlined, SettingOutlined } from "@ant-design/icons";
import {
  BusinessColumnOption,
  PINNED_LAST_COLUMN_KEY,
} from "../../../utils/businessColumnPrefs";
import styles from "./BusinessColumnSettings.module.css";

interface BusinessColumnSettingsProps {
  options: BusinessColumnOption[];
  onOrderChange: (order: string[]) => void;
  onVisibilityChange: (key: string, visible: boolean) => void;
  onReset: () => void;
}

interface ColumnRowProps {
  option: BusinessColumnOption;
  onVisibilityChange: (key: string, visible: boolean) => void;
}

const ColumnRow: React.FC<ColumnRowProps> = ({
  option,
  onVisibilityChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const checkbox = (
    <Checkbox
      checked={option.visible}
      disabled={option.locked}
      onChange={(event) => onVisibilityChange(option.key, event.target.checked)}
    >
      {option.label}
    </Checkbox>
  );

  return (
    <div ref={setNodeRef} style={style} className={styles.row}>
      {option.key === PINNED_LAST_COLUMN_KEY ? (
        <span className={styles.handle_pinned} aria-hidden>
          <HolderOutlined />
        </span>
      ) : (
        <span
          className={styles.handle}
          {...attributes}
          {...listeners}
          aria-label={`Reorder ${option.label}`}
        >
          <HolderOutlined />
        </span>
      )}
      {option.locked ? (
        <Tooltip title="This column cannot be hidden">
          <span>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      )}
    </div>
  );
};

const BusinessColumnSettings: React.FC<BusinessColumnSettingsProps> = ({
  options,
  onOrderChange,
  onVisibilityChange,
  onReset,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const hiddenCount = options.filter((option) => !option.visible).length;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = options.map((option) => option.key);
    const oldIndex = keys.indexOf(String(active.id));
    const newIndex = keys.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onOrderChange(arrayMove(keys, oldIndex, newIndex));
  };

  const content = (
    <div className={styles.panel}>
      <Typography.Text type="secondary" className={styles.hint}>
        Drag to reorder, untick to hide. Saved on this device only.
      </Typography.Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={options.map((option) => option.key)}
          strategy={verticalListSortingStrategy}
        >
          {options.map((option) => (
            <ColumnRow
              key={option.key}
              option={option}
              onVisibilityChange={onVisibilityChange}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type="link"
        size="small"
        onClick={onReset}
        className={styles.reset}
      >
        Reset to default
      </Button>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      destroyOnHidden
    >
      <Button
        icon={<SettingOutlined />}
        className={styles.trigger}
        type="default"
      >
        Configure columns
        {hiddenCount
          ? ` · ${options.length - hiddenCount}/${options.length}`
          : ""}
      </Button>
    </Popover>
  );
};

export default BusinessColumnSettings;
